import { useRef } from 'react';

// Helper to parse address components (Shared by both hooks)
const parsePlaceComponents = (place) => {
    let _address_line = "";
    let _address_city = "";
    let _address_state = "";
    let _address_zip = "";
    let _address_country = "";
    let _route_exists = false;
    let _country_code = "";

    const components = place.address_components || [];

    for (const component of components) {
        if (component.types.includes("route")) _route_exists = true;
        if (component.types.includes("country")) _country_code = component.short_name;
    }

    components.forEach((component) => {
        if (component.types.includes("street_number")) {
            _address_line = component.long_name;
        } else if (component.types.includes("route")) {
            _address_line += " " + component.long_name;
        } else if (!_route_exists && component.types.includes("administrative_area_level_2")) {
            _address_line += " " + component.long_name;
        } else if (component.types.includes("locality") || component.types.includes("postal_town")) {
            _address_city = component.long_name;
        } else if (component.types.includes("administrative_area_level_1") && _country_code === "US") {
            _address_state = component.short_name;
        } else if (component.types.includes("postal_code")) {
            _address_zip = component.long_name;
        } else if (component.types.includes("country")) {
            if (_country_code !== "US") _address_state = component.long_name;
            _address_country = component.short_name;
        }
    });

    if (_address_line === "" && place.formatted_address) {
        try {
            const _f_address_pieces = place.formatted_address.split(',');
            if (_f_address_pieces[0] !== "") _address_line = _f_address_pieces[0].trim();
        } catch (e) {}
    }

    return { _address_line, _address_city, _address_state, _address_zip, _address_country };
};

export function useGoogleAutocompleteUniversal(onPlaceParsed, onManualSearch) {
    // This is the variable the error was complaining about
    const containerRef = useRef(null);
    const autocompleteInstance = useRef(null);

    const initAutocomplete = async () => {
        if (!window.google) return;

        // If we already created the input, just put it in the new container
        if (autocompleteInstance.current) {
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
                containerRef.current.appendChild(autocompleteInstance.current);
            }
            return; // Now it's safe to return
        }

        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary("places");

        const autocomplete = new PlaceAutocompleteElement();
        autocomplete.style.width = '100%';

        autocomplete.addEventListener('gmp-select', async (event) => {
            console.log("a selection was made from the autocomplete list");

            const place = event.placePrediction.toPlace();

            // Fetch everything so we can "sniff" if it's a house or a business
            await place.fetchFields({
                fields: [
                    'displayName', 'formattedAddress', 'location', 'addressComponents',
                    'id', 'photos', 'websiteURI', 'internationalPhoneNumber',
                    'openingHours', 'priceLevel', 'rating', 'userRatingCount', 'types'
                ]
            });

            console.log("fetching  : ", place);
            const isBusiness = place.types.includes('establishment') || place.types.includes('point_of_interest');

            onPlaceParsed(place, isBusiness);
        });

        // FIX: Listen for 'Enter' on the internal input
        autocomplete.addEventListener('keyup', (e) => {

            if (e.key === 'Enter') {
                //e.preventDefault();
                // Get the text directly from the component's internal state
                const query = autocomplete.value;
                if (query) onManualSearch(query);
                autocomplete.value = '';
            }
        });

        autocompleteInstance.current = autocomplete;

        if (containerRef.current) {
            containerRef.current.innerHTML = '';
            containerRef.current.appendChild(autocomplete);
        }
    };

    // We return 'containerRef' here, which you rename to 'containerRefUniversal' above
    return { containerRef, initAutocomplete, autocompleteInstance };
}

export function useGoogleAutocompleteForAddress(onPlaceParsed) {
    const inputRefResidential = useRef(null);
    const autocompleteRefResidential = useRef(null);
    const hasInitialized = useRef(false);
    const pacContainerRef = useRef(null);

    const attachAutocompleteResidential = () => {
        if (!window.google?.maps || !inputRefResidential.current || autocompleteRefResidential.current) return;

        autocompleteRefResidential.current = new window.google.maps.places.Autocomplete(inputRefResidential.current, {
            types: ['address'],
            // CRITICAL: Specifying fields removes the "Suboptimal Performance" warning
            fields: ['address_components', 'geometry', 'formatted_address', 'place_id', 'name']
        });

        autocompleteRefResidential.current.addListener('place_changed', () => {
            const place = autocompleteRefResidential.current.getPlace();
            if (!place.geometry) return;

            const parsed = parsePlaceComponents(place); // Use the helper from previous turn

            onPlaceParsed({
                google_place_id: place.place_id || '',
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
                formatted_address: place.formatted_address || '',
                address: parsed._address_line,
                city: parsed._address_city,
                state: parsed._address_state,
                zip: parsed._address_zip,
                country: parsed._address_country,
            }, place);
        });
    };

    const removePacContainerResidential = () => {
        if (pacContainerRef.current) {
            pacContainerRef.current.remove();
            pacContainerRef.current = null;
            hasInitialized.current = false;
        }
    };

    return { inputRefResidential, attachAutocompleteResidential, removePacContainerResidential };
}

export function useGoogleAutocompleteForEstablishment(onPlaceParsed) {
    const inputRefEstablishment = useRef(null);
    const autocompleteRefEstablishment = useRef(null);
    const hasInitialized = useRef(false);
    const pacContainerRef = useRef(null);

    const attachAutocompleteEstablishment = () => {
        if (!window.google?.maps || !inputRefEstablishment.current || autocompleteRefEstablishment.current) return;

        autocompleteRefEstablishment.current = new window.google.maps.places.Autocomplete(inputRefEstablishment.current, {
            types: ['establishment'],
            // CRITICAL: Explicit fields for establishments
            fields: [
                'address_components', 'geometry', 'formatted_address', 'place_id', 'name',
                'international_phone_number', 'opening_hours', 'website', 'photos',
                'price_level', 'rating', 'user_ratings_total', 'types'
            ]
        });

        autocompleteRefEstablishment.current.addListener('place_changed', () => {
            const place = autocompleteRefEstablishment.current.getPlace();
            if (!place || !place.geometry) return;

            const parsed = parsePlaceComponents(place);
            const internationalPhone = place.international_phone_number || "";
            let phone_prefix = "", phone = "";

            if (internationalPhone.startsWith("+")) {
                const spaceIdx = internationalPhone.indexOf(" ");
                if (spaceIdx !== -1) {
                    phone_prefix = internationalPhone.substring(0, spaceIdx);
                    phone = internationalPhone.substring(spaceIdx + 1);
                } else {
                    phone_prefix = internationalPhone;
                }
            }

            onPlaceParsed({
                google_place_id: place.place_id || '',
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
                name: place.name,
                formatted_address: place.formatted_address,
                address: parsed._address_line,
                city: parsed._address_city,
                state: parsed._address_state,
                zip: parsed._address_zip,
                country: parsed._address_country,
                phone_prefix,
                phone,
                types: place.types,
                hours: place.opening_hours?.weekday_text,
                website: place.website,
                photos: place.photos,
                price_level: place.price_level,
                rating: place.rating,
                total_ratings: place.user_ratings_total
            }, place);
        });
    };

    const removePacContainerEstablishment = () => {
        if (pacContainerRef.current) {
            pacContainerRef.current.remove();
            pacContainerRef.current = null;
            hasInitialized.current = false;
        }
    };

    return { inputRefEstablishment, attachAutocompleteEstablishment, removePacContainerEstablishment };
}

// Internal Helper for the PAC container logic
function setupMutationObserver(ref) {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.addedNodes) {
                Array.from(mutation.addedNodes).forEach((node) => {
                    if (node.classList && node.classList.contains('pac-container')) {
                        ref.current = node;
                    }
                });
            }
        });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
}